(ns shared.core.utils.sql
  "Pretty-printing for the SQL a report component actually ran. `build-bq-query-str`
   joins every clause with spaces, so what comes back is one long line — unreadable
   as-is. `format-query` breaks it onto clause boundaries and indents by subquery
   depth.

   Purely cosmetic: the result is only ever displayed, never re-parsed, so the rule
   is to break where it is unambiguous and leave everything else exactly as written.
   Text inside a string literal is untouched, and a paren only earns line breaks and
   indentation when it opens a real subquery — so `count(distinct x)`,
   `extract(day from ts)` and `over (partition by a order by b)` stay on one line."
  (:require [clojure.string :as s]))

(def ^:private break-keywords
  "Keywords that start a new line, longest first so the longer of two overlapping
   keywords wins (`union all` over `union`, `left join` over `join`)."
  ["union all" "union"
   "left outer join" "right outer join" "full outer join"
   "left join" "right join" "full join" "inner join" "cross join" "join"
   "group by" "order by"
   "select" "from" "where" "having" "qualify" "window" "limit" "with"
   "on" "and" "or"])

(def ^:private nested-keywords
  "Continuations of the clause above them, so they indent one level further."
  #{"on" "and" "or"})

(def ^:private quote-chars #{\' \" \`})

(defn- word-char? [c]
  (boolean (some->> c (str) (re-find #"[A-Za-z0-9_$]"))))

(defn- keyword-at
  "The break keyword `sql` carries at `i`, or nil. Matched on word boundaries so an
   identifier that merely contains one (`my_from`, `android`) is left alone."
  [sql i]
  (when-not (word-char? (get sql (dec i)))
    (some (fn [kw]
            (let [end (+ i (count kw))]
              (when (and (<= end (count sql))
                         (= kw (s/lower-case (subs sql i end)))
                         (not (word-char? (get sql end))))
                kw)))
          break-keywords)))

(defn- subquery-paren?
  "Whether the `(` at `i` opens a subquery rather than a function's arguments — i.e.
   whether the first word inside it starts a query. Lookahead is bounded; a subquery
   always announces itself immediately."
  [sql i]
  (let [ahead (subs sql (inc i) (min (count sql) (+ i 40)))]
    (boolean (re-find #"(?i)^\s*(select|with)\b" ahead))))

(defn- indent [depth extra]
  (apply str (repeat (+ depth extra) "  ")))

(defn format-query
  "`sql` broken onto clause boundaries and indented by subquery depth. Returns nil
   for blank input."
  [sql]
  (let [sql (some-> sql (str) (s/trim))
        n (count sql)]
    (when (not-empty sql)
      (loop [i 0
             out []
             ;; One entry per open paren, true when it opens a subquery. Its count of
             ;; trues is the indent depth, and only its innermost entry decides whether
             ;; a keyword here may break.
             parens []
             quote nil]
        (if (>= i n)
          (-> (apply str out)
              (s/replace #"[ \t]+\n" "\n")
              (s/trim))
          (let [c (get sql i)
                depth (count (filterv identity parens))]
            (cond
              quote
              (recur (inc i) (conj out c) parens (when-not (= c quote) quote))

              (contains? quote-chars c)
              (recur (inc i) (conj out c) parens c)

              (= c \()
              (let [sub? (subquery-paren? sql i)]
                (recur (inc i) (conj out c) (conj parens sub?) nil))

              (= c \))
              (let [sub? (peek parens)
                    parens (cond-> parens (not-empty parens) (pop))]
                (recur (inc i)
                       (cond-> out
                         sub? (conj "\n" (indent (dec depth) 0))
                         true (conj c))
                       parens
                       nil))

              :else
              (if-let [kw (and (or (empty? parens) (peek parens))
                               (keyword-at sql i))]
                (recur (+ i (count kw))
                       (conj out "\n" (indent depth (if (nested-keywords kw) 1 0))
                             (subs sql i (+ i (count kw))))
                       parens
                       nil)
                (recur (inc i) (conj out c) parens nil)))))))))
